import axios from 'axios';

// History entry interface
export interface StatusHistoryEntry {
  timestamp: string;
  status: string;
  subStatus: string | null;
  updatedBy: string;
  note: string;
}

// Job interface matching the API response
export interface Job {
  uuid: string;
  status: string;
  subStatus: string | null;
  assignedTo: string | null;
  formType: string;
  clientName: string;
  statusUpdatedAt: string;
  submittedDate: string;
  formSheetName: string;
  clinic: string;
  statusHistory?: StatusHistoryEntry[];
}

// API response interface
interface JobsApiResponse {
  success: boolean;
  clinic: string;
  jobs: Job[];
  error?: string;
}

// Function to get jobs through our proxy endpoint
export const getJobs = async (): Promise<{ jobs: Job[], error: string | null }> => {
  try {
    // Use the server's proxy endpoint instead of direct API access
    const response = await axios.get<JobsApiResponse>('/api/clinic-jobs/BEC');

    if (response.data.success) {
      return { jobs: response.data.jobs, error: null };
    } else {
      return { jobs: [], error: response.data.error || 'Failed to fetch jobs' };
    }
  } catch (err) {
    console.error('Error fetching jobs from API:', err);
    return { jobs: [], error: 'Failed to connect to jobs API. Please ensure the slack-form-handler service is running.' };
  }
};

// Format helpers
export const formatFormType = (formType: string): string => {
  return formType
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString();
};

// Get appropriate color for status chip
export const getStatusColor = (status: string): 'default' | 'success' | 'info' | 'warning' | 'error' => {
  switch(status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'in progress':
      return 'info';
    case 'pending':
      return 'warning';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

// Get appropriate TimelineDot color for status
export const getTimelineDotColor = (status: string): 'grey' | 'success' | 'primary' | 'warning' | 'error' => {
  switch(status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'in progress':
      return 'primary';
    case 'pending':
      return 'warning';
    case 'cancelled':
      return 'error';
    default:
      return 'grey';
  }
};

// Interface for form data response
export interface FormDataResponse {
  success: boolean;
  uuid: string;
  formType: string;
  formData: Record<string, string>;
  rawData: Array<{question: string, value: string}>;
  error?: string;
  note?: string;
}

// Function to fetch form data for a specific job
export const getFormData = async (formType: string, uuid: string): Promise<FormDataResponse | null> => {
  try {
    // Use the proxy endpoint to avoid CORS issues
    const response = await axios.get<FormDataResponse>(`/api/form-data/${formType}/${uuid}`);

    // Log the API response for debugging
    console.log('Form data API response:', response.data);
    
    if (response.data.success) {
      return response.data;
    } else {
      console.error('Error in form data response:', response.data.error);
      throw new Error(response.data.error || 'API returned unsuccessful response');
    }
  } catch (error) {
    console.error('Error fetching form data:', error);
    throw error;
  }
};